<script>
    import { apiUrl } from "@/utils";
    import MediaCardTitle from "./templates/MediaCardTitle";
    import MediaCardDescription from "./templates/MediaCardDescription";
    import MediaCardLabel from "./templates/MediaCardLabel";
    import MediaCardLink from "./templates/MediaCardLink";

    export default {
        name: "TheMediaListCard",
        components: { MediaCardLink, MediaCardLabel, MediaCardDescription, MediaCardTitle },
        props: {
            media: Object,
            showDescription: Boolean,
            showLabel: Boolean,
            showResource: Boolean,
            /** @type {import('vue').PropOptions<"inside" | "outside">} */
            titlePosition: {
                type: String,
                default: "outside",
                validator: (value) => {
                    return ["inside", "outside"].includes(value);
                },
            },
            imageHeight: [Number, String],
        },
        computed: {
            isVideo() {
                return this.media.idVideo;
            },
            typeLabel() {
                return this.isVideo ? "video" : "article";
            },
            nameLabel() {
                return this.isVideo ? "Видео" : "Статья";
            },
            updatedSrc() {
                return this.isVideo
                    ? `https://i.ytimg.com/vi/${this.media.idVideo}/hqdefault.jpg`
                    : apiUrl(this.media.previewImage);
            },
            tag() {
                return this.isVideo ? "div" : "a";
            },
            source() {
                return new URL(this.media.externalUrl).hostname;
            },
        },
        methods: {
            openVideoModal() {
                if (this.isVideo) {
                    this.$popup.show("modal-video", { videoId: this.isVideo, autoplay: true });
                }
            },
        },
    };
</script>

<template>
    <component
        :is="tag"
        :href="media.externalUrl"
        target="_blank"
        class="media-card outline"
        @click="openVideoModal"
        @keypress.enter="openVideoModal"
        tabindex="0"
    >
        <div class="media-card__header">
            <media-card-label v-if="showLabel" :type="typeLabel">
                {{ nameLabel }}
            </media-card-label>
            <div v-if="showLabel" class="media-card__source">{{ source }}</div>
        </div>
        <div class="media-card__wrap">
            <img class="media-card__img swiper-lazy" alt="" :data-src="updatedSrc" />
            <div class="swiper-lazy-preloader">
                <preloader :show="true" lg-size></preloader>
            </div>
            <media-card-title
                v-if="media.name && titlePosition === 'inside'"
                :position="titlePosition"
            >
                {{ media.name }}
            </media-card-title>
        </div>

        <media-card-title
            v-if="media.name && titlePosition === 'outside'"
            :position="titlePosition"
        >
            {{ media.name }}
        </media-card-title>

        <media-card-description v-if="media.previewDescription && showDescription">
            {{ media.previewDescription }}
        </media-card-description>

        <media-card-link v-if="showResource" :url="media.externalUrl"></media-card-link>
    </component>
</template>

<style lang="scss">
    .media-card {
        display: block;
        width: calc(100% - 16px);
        padding-right: 16px;
        position: relative;
        margin-bottom: 8px;
        cursor: pointer;

        @include bp($bp-mobile-sm) {
            width: 100%;
            padding-right: 0;
        }

        @include bp($bp-desktop-sm) {
            &:hover {
                .media-card__img {
                    transform: translateZ(0) scale3d(1.05, 1.05, 1.05);
                }
            }
        }
    }

    .media-card__header {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
    }

    .media-card__source {
        color: var(--media-source);
        font-size: 14px;
        font-weight: var(--media-source-weight);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1 2 auto;
    }

    .media-card__wrap {
        position: relative;
        padding-bottom: var(--media-card-ratio);
        overflow: hidden;
        margin-bottom: 8px;
        border-radius: var(--media-card-radius);
    }

    .media-card__img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: 0.3s ease-in-out all;
        -webkit-backface-visibility: hidden;
        transform: translateZ(0);
        border-radius: var(--media-card-radius);
    }
</style>
